/**
 * React context for the Agent Panel.
 *
 * Owns ONE :class:`SessionRuntime` at a time — the foreground
 * conversation. Switching sessions disposes the prior runtime and
 * spins up a fresh one for the new ``(cwd, sessionId)``. Background
 * sessions stay alive on the *server*, not the client: the agent
 * server's :class:`LiveSession` machinery survives intentional
 * session switches and transient WebSocket drops, keeps the SDK
 * subprocess running, and reattaches us via ``?since_seq=`` replay
 * when needed. Normal browser focus / tab visibility changes are not
 * lifecycle events for the runtime.
 *
 * Owns:
 *   - Overlay visibility (persisted via the shared overlay map).
 *   - Active cwd + per-cwd resume session id + per-cwd effort
 *     (persisted to localStorage).
 *   - The single active :class:`SessionRuntime` and its lifecycle.
 *
 * Sidebar live status (in-progress / unread dots for sessions the
 * user isn't currently looking at) comes from the workspace server's
 * ``/project/sessions`` endpoint, which lists ``is_active`` and
 * ``last_event_at`` per session. The sidebar polls that endpoint
 * directly; it doesn't need a pool of in-browser runtimes anymore.
 */

import * as React from 'react';
import { type AgentSessionEntry, configureAgentApi } from '@/lib/agent/api';
import { type AgentPanelState, type ConnectionStatus, initialState, type TurnDisplay } from '@/lib/agent/reducer';
import {
  loadActiveCwd,
  loadEffort,
  loadSessionId,
  loadSessionPrefs,
  patchSessionPrefs,
  saveActiveCwd,
  saveEffort,
  saveSessionId,
  sweepLegacyUnscopedEntries,
  workspaceScopedKey,
} from '@/lib/agent/session-persistence';
import { type SendInputResult, SessionRuntime, type SessionRuntimeSnapshot } from '@/lib/agent/sessionRuntime';
import type { AgentSpeed, PermissionMode } from '@/lib/agent/types';
import { isAgentSupportedForWorkspace } from '@/lib/agent/versionGate';
import { getOverlayVisibility, updateOverlayVisibility } from '@/lib/persistence';
import { isLocalTarget } from '@/lib/utils/workspace';
import { useServiceTokens } from '@/providers/ServiceTokensProvider';
import { useWorkspace } from '@/providers/WorkspaceProvider';
import { disposeAttachmentStore } from './AgentInput';

export interface AgentPanelContextValue {
  // Visibility
  isAgentPanelVisible: boolean;
  setAgentPanelVisible: (visible: boolean) => void;

  // Picker — the two-step project/session chooser. Implicitly visible
  // when no cwd is set; toggled by the user to switch projects.
  pickerVisible: boolean;
  /** Open the picker fresh — always lands on the file browser at $HOME. */
  openPicker: () => void;
  closePicker: () => void;

  // Session wiring
  /** Workspace this panel is bound to (``null`` when none connected). */
  workspaceId: string | null;
  cwd: string | null;
  resumeSessionId: string | null;
  /**
   * Switch (or open) a session for ``(cwd, resumeSessionId)``.
   * Disposes the prior runtime if any; the server-side LiveSession
   * for the prior session keeps running in the background and is
   * reattached if the user navigates back. Idempotent on identical
   * inputs.
   */
  chooseSession: (cwd: string, resumeSessionId: string | null) => void;
  /**
   * Cached metadata for the active session (name, created_at,
   * last_modified). Refreshes after every ``session_attached`` and
   * after the user renames the session in-place.
   */
  currentSession: AgentSessionEntry | null;
  /**
   * Rename the active session in-place. Refreshes
   * :attr:`currentSession` so the header label reflects the new
   * title without a manual refetch.
   */
  renameCurrentSession: (title: string) => Promise<void>;

  // Reducer state — drawn from the active runtime's snapshot.
  state: AgentPanelState;
  connectionStatus: ConnectionStatus;
  attachmentRole: SessionRuntimeSnapshot['attachmentRole'];
  attachmentCount: SessionRuntimeSnapshot['attachmentCount'];
  pendingUserInputIds: string[];
  lastAcceptedClientMessageId: string | null;
  /** True once REST/WS calls to the workspace agent API have the routing + auth they need. */
  agentApiReady: boolean;

  // Chat history pagination — handled by the active runtime.
  historyHasMore: boolean;
  historyLoading: boolean;
  loadOlderHistory: () => Promise<void>;

  /**
   * ``true`` while the panel is still standing up the chat for the
   * current ``(cwd, resumeSessionId)`` — the WS hasn't sent
   * ``session_attached`` yet, OR (for resumed sessions) the initial
   * history fetch / session-metadata fetch hasn't resolved yet.
   */
  sessionLoading: boolean;

  // Commands
  sendInput: (text: string, display?: TurnDisplay) => SendInputResult;
  respondToInteraction: (
    requestId: string,
    decision: 'allow' | 'deny',
    updatedInput?: Record<string, unknown>,
    message?: string
  ) => void;
  interrupt: () => void;
  /**
   * Switch the SDK's reasoning effort. Delegates to the active
   * runtime (which forces a reconnect with the new ``?effort=``)
   * and persists the per-cwd preference.
   */
  setEffort: (effort: string) => void;
  /** Switch the agent between normal speed and Claude Code fast mode. */
  setSpeed: (speed: AgentSpeed) => void;
  /** Switch the agent between native plan mode and execute mode. */
  setPermissionMode: (mode: PermissionMode) => void;
  stopTask: (taskId: string) => void;
  /** Clear the sticky error banner — both server errors and rate-limit rejections. */
  clearErrors: () => void;
  /**
   * Drop the current WS, clear the sticky error state, and force
   * the active runtime to dial again. Used by the full-pane crash
   * screen's "Retry" button.
   */
  retrySession: () => void;
}

/**
 * Internal context for the provider tree and story/test fixtures.
 */
export const AgentPanelContext = React.createContext<AgentPanelContextValue | null>(null);

const ENABLED_FLAG = 'VITE_AGENT_PANEL_ENABLED';
const MAX_CACHED_RUNTIMES = 2;

function flagEnabled(raw: string | undefined): boolean {
  if (raw === undefined) return true;
  return raw !== 'false' && raw !== '0';
}

/**
 * Subscribe to a runtime via React's :func:`useSyncExternalStore`.
 * The returned snapshot is ``null`` when no runtime is bound.
 */
function useRuntimeSnapshot(runtime: SessionRuntime | null): SessionRuntimeSnapshot | null {
  const subscribe = React.useMemo(() => {
    if (!runtime) return () => () => undefined;
    return runtime.subscribe;
  }, [runtime]);
  const getSnapshot = React.useMemo(() => {
    if (!runtime) return () => null;
    return runtime.getSnapshot;
  }, [runtime]);
  return React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

function runtimeKey(workspaceId: string | null, cwd: string, sessionId: string | null): string {
  // ``workspaceId`` is part of the identity: a runtime built for one
  // workspace is bound to that workspace's agent server, and two
  // workspaces frequently share a ``cwd`` path. Without it in the key,
  // switching workspaces could resolve the previous workspace's cached
  // runtime — replaying its entire chat against a different sim.
  return workspaceScopedKey(workspaceId, cwd, sessionId ?? '__new__');
}

export interface AgentRuntimeVisibilityInput {
  agentSupported: boolean;
  agentApiReady: boolean;
  isAgentPanelVisible: boolean;
  hasStreamTarget: boolean;
}

export interface AgentRuntimeVisibility {
  shouldBindRuntime: boolean;
  runtimeVisible: boolean;
}

export function deriveAgentRuntimeVisibility({
  agentSupported,
  agentApiReady,
  isAgentPanelVisible,
  hasStreamTarget,
}: AgentRuntimeVisibilityInput): AgentRuntimeVisibility {
  const shouldBindRuntime = agentSupported && agentApiReady && isAgentPanelVisible && hasStreamTarget;
  return {
    shouldBindRuntime,
    runtimeVisible: shouldBindRuntime,
  };
}

export function AgentPanelProvider({ children }: { children: React.ReactNode }) {
  // Drop pre-workspace-scoping storage once per mount — those entries
  // can never match a NUL-scoped lookup again.
  React.useEffect(() => {
    sweepLegacyUnscopedEntries();
  }, []);

  // Visibility persists across reloads via the shared overlay-visibility
  // map (same one ``StreamOnlyWindow`` uses for the notebook / docs /
  // debug overlays).
  const [isAgentPanelVisible, setAgentPanelVisibleState] = React.useState<boolean>(
    () => getOverlayVisibility().antiochAgent ?? false
  );
  const setAgentPanelVisible = React.useCallback((visible: boolean) => {
    setAgentPanelVisibleState(visible);
    updateOverlayVisibility('antiochAgent', visible);
  }, []);

  const [pickerVisible, setPickerVisible] = React.useState(false);

  // Routing + auth context for /workspace/agent/* traffic. The webapp lives at
  // ``app.antioch.com``; the workspace server lives behind Envoy at the
  // per-workspace hostname, so every WS / fetch must target the
  // workspace's HTTPS origin and carry the workspace JWT. We read both
  // from the existing WorkspaceProvider / ServiceTokensProvider. Local
  // targets are different: their ``httpBaseUrl`` points at the sim status
  // server on :8081, while /workspace/* should stay same-origin so Vite can
  // proxy it to localhost:8899 through Cursor's forwarded port.
  const { connectedWorkspace, streamTarget } = useWorkspace();
  const workspaceId = connectedWorkspace?.id ?? null;

  // Rehydrate the (cwd, sessionId) pointer in one shot on mount so
  // the auto-create effect fires once with both set instead of
  // churning a connect / disconnect cycle as each piece of state lands.
  //
  // Scoping by ``workspaceId`` keeps a cwd remembered for one workspace
  // (e.g. ``/home/<user>/project`` on a local-dev workspace) from
  // leaking into a different workspace where that path does not exist.
  const initialCwd = React.useMemo(() => loadActiveCwd(workspaceId), [workspaceId]);
  const [cwd, setCwd] = React.useState<string | null>(initialCwd);
  const [resumeSessionId, setResumeSessionId] = React.useState<string | null>(() =>
    initialCwd ? loadSessionId(workspaceId, initialCwd) : null
  );

  // When the connected workspace changes, re-hydrate the (cwd, sessionId)
  // pointer for the new workspace. This is a render-time state adjustment
  // (React's "derived state from props" pattern), NOT a passive effect:
  // WorkspaceProvider can switch the connected workspace directly from
  // A to B without passing through null, and an effect-based rehydrate
  // would leave one full commit where ``workspaceId`` is B while ``cwd``
  // / ``resumeSessionId`` are still A's — every effect in that commit
  // would then run against the mixed state (binding a junk runtime,
  // persisting A's pointers under B's keys). Adjusting during render
  // makes React discard the mixed render before children or effects
  // ever observe it. The picker opens on its own when cwd is null, so
  // we do not need to force pickerVisible here.
  const [hydratedWorkspaceId, setHydratedWorkspaceId] = React.useState<string | null>(workspaceId);
  if (hydratedWorkspaceId !== workspaceId) {
    setHydratedWorkspaceId(workspaceId);
    const nextCwd = loadActiveCwd(workspaceId);
    setCwd(nextCwd);
    setResumeSessionId(nextCwd ? loadSessionId(workspaceId, nextCwd) : null);
  }
  const { tokens: serviceTokens } = useServiceTokens();
  const baseUrl = streamTarget && !isLocalTarget(streamTarget) ? streamTarget.httpBaseUrl : null;
  const workspaceToken = baseUrl ? (serviceTokens?.workspace_token ?? null) : null;
  const agentSupported = isAgentSupportedForWorkspace({
    target: streamTarget,
    workspaceVersion: connectedWorkspace?.version ?? null,
  });
  const agentApiReady = baseUrl === null || workspaceToken !== null;
  // Child effects (SessionSidebar initial load, runtime metadata
  // refreshes) can run before this provider's passive effects. Keep
  // the module-level agent REST client in sync during render so the
  // first child fetch after ``agentApiReady`` flips cannot race ahead
  // with a stale or missing workspace token.
  configureAgentApi({ baseUrl, workspaceToken });
  const { shouldBindRuntime, runtimeVisible } = deriveAgentRuntimeVisibility({
    agentSupported,
    agentApiReady,
    isAgentPanelVisible,
    hasStreamTarget: streamTarget !== null,
  });
  const connectionRef = React.useRef<{ baseUrl: string | null; workspaceToken: string | null }>({
    baseUrl,
    workspaceToken,
  });
  connectionRef.current = { baseUrl, workspaceToken };

  // Foreground runtime plus a small browser-side runtime cache. The
  // workspace server keeps SDK LiveSessions alive, but keeping recent
  // browser runtimes avoids blank history rehydration when the user
  // bounces between sessions in the sidebar.
  const runtimeRef = React.useRef<SessionRuntime | null>(null);
  const runtimeCacheRef = React.useRef<Map<string, SessionRuntime>>(new Map());
  const activeRuntimeKeyRef = React.useRef<string | null>(null);
  // Which workspace the cached runtimes belong to. Agent sessions are
  // workspace-server-resident, so a runtime built for one workspace is
  // meaningless (and actively harmful — it carries the prior chat) for
  // another. When this no longer matches the connected workspace, the
  // whole cache is torn down before anything rebinds.
  const cacheWorkspaceRef = React.useRef<string | null>(null);
  const [, forceRuntimeRebind] = React.useReducer((n: number) => n + 1, 0);

  // Bind the foreground runtime whenever the active (cwd, sessionId)
  // or bindability changes. Browser focus, document visibility, and
  // top-level app tabs are intentionally absent from this lifecycle:
  // users often keep the workspace running while editing in an IDE or
  // inspecting another browser tab, and that must not reset chat state.
  //
  // Preferred effort resolution order:
  //   1. Per-(cwd, sessionId) saved prefs — what the user explicitly
  //      set last time they were in THIS session.
  //   2. Per-cwd default — last effort the user picked anywhere in
  //      this project, used for fresh sessions or sessions with no
  //      explicit pref.
  //   3. ``null`` — let the workspace server fall back to settings.json.
  React.useEffect(() => {
    // Workspace switch: tear the cache down before anything rebinds.
    // The runtime key already folds in ``workspaceId`` so a stale entry
    // could never be *resolved* for the new workspace, but the old
    // runtimes are still live (background WS, SDK attachment) and must
    // be disposed rather than leaked. ``workspaceId`` is in this
    // effect's deps so a switch re-runs it even when ``cwd`` is byte-
    // identical across the two workspaces.
    if (cacheWorkspaceRef.current !== workspaceId) {
      for (const cached of runtimeCacheRef.current.values()) {
        // These runtimes belong to the *previous* workspace, so dispose their
        // attachment stores under that workspace id (the key is workspace-scoped).
        disposeAttachmentStore(cacheWorkspaceRef.current, cached.cwd, cached.sessionId);
        cached.dispose();
      }
      runtimeCacheRef.current.clear();
      runtimeRef.current = null;
      activeRuntimeKeyRef.current = null;
      cacheWorkspaceRef.current = workspaceId;
    }

    const previousKey = activeRuntimeKeyRef.current;
    if (previousKey) {
      runtimeCacheRef.current.get(previousKey)?.setVisibility(false);
    }
    if (!cwd || !shouldBindRuntime) {
      runtimeRef.current = null;
      activeRuntimeKeyRef.current = null;
      forceRuntimeRebind();
      return;
    }

    const key = runtimeKey(workspaceId, cwd, resumeSessionId);
    let next = runtimeCacheRef.current.get(key);
    if (!next) {
      const sessionPrefs = resumeSessionId ? loadSessionPrefs(workspaceId, cwd, resumeSessionId) : null;
      const effort = sessionPrefs?.effort ?? loadEffort(workspaceId, cwd);
      const speed = sessionPrefs?.speed ?? 'normal';
      const permissionMode = sessionPrefs?.permissionMode ?? 'bypassPermissions';
      next = new SessionRuntime({
        workspaceId,
        cwd,
        resumeSessionId,
        effort,
        speed,
        permissionMode,
        visible: runtimeVisible,
        baseUrl: connectionRef.current.baseUrl,
        workspaceToken: connectionRef.current.workspaceToken,
      });
      runtimeCacheRef.current.set(key, next);
    } else {
      runtimeCacheRef.current.delete(key);
      runtimeCacheRef.current.set(key, next);
      next.setConnection(connectionRef.current);
      next.setVisibility(runtimeVisible);
    }

    for (const [cachedKey, cached] of runtimeCacheRef.current) {
      if (cachedKey !== key) cached.setVisibility(false);
    }
    while (runtimeCacheRef.current.size > MAX_CACHED_RUNTIMES) {
      const oldestKey = runtimeCacheRef.current.keys().next().value as string | undefined;
      if (oldestKey === undefined || oldestKey === key) break;
      const evicted = runtimeCacheRef.current.get(oldestKey);
      // Tear down the matching attachment store alongside the runtime so the
      // module-level Map in AgentInput.tsx can't grow without bound across a
      // long tab session (and any lingering thumbnail blob URLs get revoked).
      if (evicted) disposeAttachmentStore(workspaceId, evicted.cwd, evicted.sessionId);
      evicted?.dispose();
      runtimeCacheRef.current.delete(oldestKey);
    }

    runtimeRef.current = next;
    activeRuntimeKeyRef.current = key;
    if (runtimeVisible) next.connect();
    forceRuntimeRebind();
  }, [workspaceId, cwd, resumeSessionId, shouldBindRuntime, runtimeVisible]);

  // When the workspace token rotates mid-session, hand the new one
  // to the live runtime so its NEXT reconnect uses the fresh JWT.
  // The active WS keeps its in-flight authentication; we never
  // close it just because tokens rotated.
  React.useEffect(() => {
    for (const runtime of runtimeCacheRef.current.values()) {
      runtime.setConnection({ baseUrl, workspaceToken });
    }
  }, [baseUrl, workspaceToken]);

  // Update the runtime's connection flag. This only changes when the
  // agent pane itself is closed or no runtime can be bound, not when
  // the browser/app tab loses focus.
  React.useEffect(() => {
    runtimeRef.current?.setVisibility(runtimeVisible);
  }, [runtimeVisible]);

  // Subscribe to the active runtime's state.
  const runtime = runtimeRef.current;
  const runtimeSnap = useRuntimeSnapshot(runtime);
  const fallbackState = React.useMemo(() => initialState(), []);
  const state = runtimeSnap?.state ?? fallbackState;
  const connectionStatus = runtimeSnap?.connectionStatus ?? 'idle';
  const attachmentRole = runtimeSnap?.attachmentRole ?? null;
  const attachmentCount = runtimeSnap?.attachmentCount ?? null;
  const pendingUserInputIds = runtimeSnap?.pendingUserInputIds ?? [];
  const lastAcceptedClientMessageId = runtimeSnap?.lastAcceptedClientMessageId ?? null;
  const historyHasMore = runtimeSnap?.historyHasMore ?? false;
  const historyLoading = runtimeSnap?.historyLoading ?? false;
  const currentSession = runtimeSnap?.currentSession ?? null;
  const sessionReady = runtimeSnap?.sessionReady ?? false;
  const [attachTimedOut, setAttachTimedOut] = React.useState(false);
  React.useEffect(() => {
    setAttachTimedOut(false);
    if (!cwd || sessionReady || connectionStatus === 'error') return;
    const timer = setTimeout(() => setAttachTimedOut(true), 30_000);
    return () => clearTimeout(timer);
  }, [cwd, sessionReady, connectionStatus]);

  // ---------------------------------------------------------------------------
  // Persistence — driven off active-runtime snapshot changes
  // ---------------------------------------------------------------------------

  // The snapshot (``state`` / ``runtimeSnap``) always lags a workspace
  // switch by one commit: in the first render after the switch,
  // ``runtime`` is still the previous workspace's runtime (the bind
  // effect rebinds during the commit, not the render). Every effect
  // below persists snapshot-derived values under ``workspaceId`` keys,
  // so each one must refuse to write when the snapshot's owning
  // workspace is not the current one — otherwise a direct A→B switch
  // writes A's session id / prefs under B's keys (the exact
  // cross-workspace bleed this provider's scoping exists to prevent).
  const snapshotOwnedByWorkspace = runtime === null || runtime.workspaceId === workspaceId;

  // Persist session_id per cwd whenever the active runtime resolves
  // to a real id.
  React.useEffect(() => {
    if (!snapshotOwnedByWorkspace) return;
    if (cwd && state.sessionId) saveSessionId(workspaceId, cwd, state.sessionId);
  }, [snapshotOwnedByWorkspace, workspaceId, cwd, state.sessionId]);

  // Fresh sessions start under a temporary ``resumeSessionId=null``
  // cache key. Once the server assigns the real id, migrate the
  // cached runtime so later sidebar clicks reuse this exact snapshot.
  React.useEffect(() => {
    if (!snapshotOwnedByWorkspace) return;
    if (!cwd || resumeSessionId !== null || !state.sessionId) return;
    const freshKey = runtimeKey(workspaceId, cwd, null);
    const realKey = runtimeKey(workspaceId, cwd, state.sessionId);
    const runtime = runtimeCacheRef.current.get(freshKey);
    if (runtime) {
      runtimeCacheRef.current.delete(freshKey);
      runtimeCacheRef.current.set(realKey, runtime);
      activeRuntimeKeyRef.current = realKey;
    }
    setResumeSessionId(state.sessionId);
  }, [snapshotOwnedByWorkspace, workspaceId, cwd, resumeSessionId, state.sessionId]);

  // Persist effort whenever it advertises a non-null value:
  //   - Per cwd, as a "default for this project's next session" hint.
  //   - Per-(cwd, sessionId), so returning to THIS specific session
  //     restores the same knobs the user had set when they left.
  React.useEffect(() => {
    if (!snapshotOwnedByWorkspace || !cwd) return;
    if (runtimeSnap?.effort) saveEffort(workspaceId, cwd, runtimeSnap.effort);
    if (state.sessionId && runtimeSnap?.effort) {
      patchSessionPrefs(workspaceId, cwd, state.sessionId, { effort: runtimeSnap.effort });
    }
  }, [snapshotOwnedByWorkspace, workspaceId, cwd, runtimeSnap?.effort, state.sessionId]);

  React.useEffect(() => {
    if (!snapshotOwnedByWorkspace || !cwd) return;
    const mode = runtimeSnap?.permissionMode;
    if (!mode) return;
    if (state.sessionId) {
      patchSessionPrefs(workspaceId, cwd, state.sessionId, { permissionMode: mode });
    }
  }, [snapshotOwnedByWorkspace, workspaceId, cwd, runtimeSnap?.permissionMode, state.sessionId]);

  React.useEffect(() => {
    if (!snapshotOwnedByWorkspace || !cwd || !state.sessionId) return;
    const speed = runtimeSnap?.speed;
    if (!speed) return;
    patchSessionPrefs(workspaceId, cwd, state.sessionId, { speed: speed === 'fast' ? speed : null });
  }, [snapshotOwnedByWorkspace, workspaceId, cwd, runtimeSnap?.speed, state.sessionId]);

  // Tear the runtime down on unmount (the effect's cleanup already
  // handles the per-change case).
  React.useEffect(() => {
    return () => {
      for (const runtime of runtimeCacheRef.current.values()) {
        runtime.dispose();
      }
      runtimeCacheRef.current.clear();
      runtimeRef.current = null;
      activeRuntimeKeyRef.current = null;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Picker
  // ---------------------------------------------------------------------------

  const openPicker = React.useCallback(() => {
    saveActiveCwd(workspaceId, null);
    setPickerVisible(true);
  }, [workspaceId]);
  const closePicker = React.useCallback(() => {
    setPickerVisible(false);
  }, []);

  // ---------------------------------------------------------------------------
  // Session switching
  // ---------------------------------------------------------------------------

  const chooseSession = React.useCallback(
    (nextCwd: string, nextSessionId: string | null) => {
      const nextKey = runtimeKey(workspaceId, nextCwd, nextSessionId);
      const previousKey = activeRuntimeKeyRef.current;

      // Session switching is driven by React state, but sends delegate
      // through ``runtimeRef`` synchronously. Park the old foreground
      // runtime immediately so a quick submit during the transition
      // cannot write into the previously selected chat.
      if (previousKey !== nextKey) {
        if (previousKey) runtimeCacheRef.current.get(previousKey)?.setVisibility(false);
        runtimeRef.current = null;
        activeRuntimeKeyRef.current = null;
        forceRuntimeRebind();
      }

      saveActiveCwd(workspaceId, nextCwd);
      setCwd(nextCwd);
      setResumeSessionId(nextSessionId);
      saveSessionId(workspaceId, nextCwd, nextSessionId);
      setPickerVisible(false);
    },
    [workspaceId]
  );

  // ---------------------------------------------------------------------------
  // Commands — delegate to the active runtime
  // ---------------------------------------------------------------------------

  const sendInput = React.useCallback(
    (text: string, display?: TurnDisplay): SendInputResult => {
      if (!cwd) return false;
      const activeKey = activeRuntimeKeyRef.current;
      const expectedKey = runtimeKey(workspaceId, cwd, resumeSessionId ?? state.sessionId);
      const freshKey = runtimeKey(workspaceId, cwd, null);
      if (
        activeKey !== expectedKey &&
        !(resumeSessionId === null && state.sessionId !== null && activeKey === freshKey)
      ) {
        return false;
      }
      return runtimeRef.current?.sendInput(text, display) ?? false;
    },
    [workspaceId, cwd, resumeSessionId, state.sessionId]
  );

  const interrupt = React.useCallback(() => {
    runtimeRef.current?.interrupt();
  }, []);

  const respondToInteraction = React.useCallback(
    (requestId: string, decision: 'allow' | 'deny', updatedInput?: Record<string, unknown>, message?: string) => {
      runtimeRef.current?.respondToInteraction(requestId, decision, updatedInput, message);
    },
    []
  );

  const setEffort = React.useCallback(
    (next: string) => {
      if (!next) return;
      if (cwd) saveEffort(workspaceId, cwd, next);
      runtimeRef.current?.setEffort(next);
    },
    [workspaceId, cwd]
  );

  const setSpeed = React.useCallback(
    (next: AgentSpeed) => {
      runtimeRef.current?.setSpeed(next);
      if (cwd && state.sessionId) {
        patchSessionPrefs(workspaceId, cwd, state.sessionId, { speed: next === 'fast' ? next : null });
      }
    },
    [workspaceId, cwd, state.sessionId]
  );

  const setPermissionMode = React.useCallback((next: PermissionMode) => {
    runtimeRef.current?.setPermissionMode(next);
  }, []);

  const stopTask = React.useCallback((taskId: string) => {
    runtimeRef.current?.stopTask(taskId);
  }, []);

  const clearErrors = React.useCallback(() => {
    runtimeRef.current?.clearErrors();
  }, []);

  const retrySession = React.useCallback(() => {
    runtimeRef.current?.retry();
  }, []);

  const loadOlderHistory = React.useCallback(async () => {
    await runtimeRef.current?.loadOlderHistory();
  }, []);

  const renameCurrentSession = React.useCallback(async (title: string) => {
    await runtimeRef.current?.renameCurrentSession(title);
  }, []);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const sessionLoading =
    cwd != null && connectionStatus !== 'error' && connectionStatus !== 'closed' && !attachTimedOut && !sessionReady;

  const value: AgentPanelContextValue = React.useMemo(
    () => ({
      isAgentPanelVisible,
      setAgentPanelVisible,
      pickerVisible,
      openPicker,
      closePicker,
      workspaceId,
      cwd,
      resumeSessionId,
      chooseSession,
      currentSession,
      renameCurrentSession,
      state,
      connectionStatus,
      attachmentRole,
      attachmentCount,
      pendingUserInputIds,
      lastAcceptedClientMessageId,
      agentApiReady,
      historyHasMore,
      historyLoading,
      loadOlderHistory,
      sessionLoading,
      sendInput,
      respondToInteraction,
      interrupt,
      setEffort,
      setSpeed,
      setPermissionMode,
      stopTask,
      clearErrors,
      retrySession,
    }),
    [
      isAgentPanelVisible,
      setAgentPanelVisible,
      pickerVisible,
      openPicker,
      closePicker,
      workspaceId,
      cwd,
      resumeSessionId,
      chooseSession,
      currentSession,
      renameCurrentSession,
      state,
      connectionStatus,
      attachmentRole,
      attachmentCount,
      pendingUserInputIds,
      lastAcceptedClientMessageId,
      agentApiReady,
      historyHasMore,
      historyLoading,
      loadOlderHistory,
      sessionLoading,
      sendInput,
      respondToInteraction,
      interrupt,
      setEffort,
      setSpeed,
      setPermissionMode,
      stopTask,
      clearErrors,
      retrySession,
    ]
  );

  if (!flagEnabled(import.meta.env[ENABLED_FLAG])) {
    return <>{children}</>;
  }

  return <AgentPanelContext.Provider value={value}>{children}</AgentPanelContext.Provider>;
}

export function useAgentPanel(): AgentPanelContextValue {
  const ctx = React.useContext(AgentPanelContext);
  if (!ctx) {
    return stubContext();
  }
  return ctx;
}

function stubContext(): AgentPanelContextValue {
  return {
    isAgentPanelVisible: false,
    setAgentPanelVisible: () => undefined,
    pickerVisible: false,
    openPicker: () => undefined,
    closePicker: () => undefined,
    workspaceId: null,
    cwd: null,
    resumeSessionId: null,
    chooseSession: () => undefined,
    currentSession: null,
    renameCurrentSession: async () => undefined,
    state: initialState(),
    connectionStatus: 'idle',
    attachmentRole: null,
    attachmentCount: null,
    pendingUserInputIds: [],
    lastAcceptedClientMessageId: null,
    agentApiReady: true,
    historyHasMore: false,
    historyLoading: false,
    loadOlderHistory: async () => undefined,
    sessionLoading: false,
    sendInput: () => false,
    respondToInteraction: () => undefined,
    interrupt: () => undefined,
    setEffort: () => undefined,
    setSpeed: () => undefined,
    setPermissionMode: () => undefined,
    stopTask: () => undefined,
    clearErrors: () => undefined,
    retrySession: () => undefined,
  };
}
